import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PartnerShopListItem, PartnerShopListResponse } from '@/types/partnerShop';
import { partnerShopListMock, allAreas } from '@/mocks/partnerShops';
import PartnerShopCard from './components/PartnerShopCard';

/* ─── Mock API ─── */
async function getPartnerShopList(params: {
  shopCategory?: string;
  areaName?: string;
  sort?: 'recommended' | 'reviewCount' | 'directReviewCount' | 'photoCount' | 'priceAsc';
  hasPromotion?: boolean;
  quickFilter?: string;
  packageType?: string;
  reviewCriteria?: string;
}): Promise<PartnerShopListResponse> {
  await new Promise((r) => setTimeout(r, 600));
  let list = [...partnerShopListMock.list];

  if (params.areaName && params.areaName !== '전체') {
    list = list.filter((s) => s.areaName === params.areaName);
  }
  if (params.hasPromotion) {
    list = list.filter((s) => s.hasPromotion);
  }

  /* Package type filter — keyword based */
  if (params.packageType && params.packageType !== 'all') {
    switch (params.packageType) {
      case 'custom':
        list = list.filter((s) =>
          s.keywords.some((kw) => ['맞춤', '맞춤예복', '기본구성', '체형보정', '클래식', '셔츠맞춤', '체형분석', '정확한핏'].includes(kw))
        );
        break;
      case 'rental':
        list = list.filter((s) => s.keywords.includes('대여가능'));
        break;
      case 'shooting':
        list = list.filter((s) => s.keywords.some((kw) => ['촬영복', '촬영복구성'].includes(kw)));
        break;
      case 'parents':
        list = list.filter((s) => s.keywords.includes('혼주예복'));
        break;
      case 'premium':
        list = list.filter((s) =>
          s.keywords.some((kw) => ['프리미엄', '프리미엄원단', '프리미엄맞춤', '고급소재', '수입원단', '핸드메이드'].includes(kw))
        );
        break;
    }
  }

  if (params.quickFilter && params.quickFilter !== 'all') {
    switch (params.quickFilter) {
      case 'cheongdam':
        list = list.filter((s) => s.areaName === '청담');
        break;
      case 'shooting':
        list = list.filter((s) => s.keywords.some((kw) => ['촬영복', '촬영복구성'].includes(kw)));
        break;
      case 'premium':
        list = list.filter((s) =>
          s.keywords.some((kw) => ['프리미엄', '프리미엄원단', '프리미엄맞춤', '고급소재', '수입원단', '핸드메이드'].includes(kw))
        );
        break;
      case 'rental':
        list = list.filter((s) => s.keywords.includes('대여가능'));
        break;
      case 'promotion':
        list = list.filter((s) => s.hasPromotion);
        break;
    }
  }

  if (params.reviewCriteria && params.reviewCriteria !== 'all') {
    switch (params.reviewCriteria) {
      case 'directReview':
        list = list.filter((s) => s.directReviewCount >= 50);
        break;
      case 'photoReview':
        list = list.filter((s) => s.reviewPhotoCount >= 20);
        break;
      case 'consultSatisfaction':
        list = list.filter((s) =>
          s.keywords.some((kw) => ['상담친절', '꼼꼼상담', '상담', '방문상담'].includes(kw))
        );
        break;
      case 'shootingConfig':
        list = list.filter((s) =>
          s.keywords.some((kw) => ['촬영복', '촬영복구성'].includes(kw))
        );
        break;
    }
  }

  const sort = params.sort || 'recommended';
  switch (sort) {
    case 'reviewCount':
      list.sort((a, b) => b.reviewCount - a.reviewCount);
      break;
    case 'directReviewCount':
      list.sort((a, b) => b.directReviewCount - a.directReviewCount);
      break;
    case 'photoCount':
      list.sort((a, b) => (b.reviewPhotoCount + b.lookbookPhotoCount) - (a.reviewPhotoCount + a.lookbookPhotoCount));
      break;
    case 'priceAsc': {
      const parsePrice = (t: string | null | undefined) => {
        if (!t) return Infinity;
        const m = t.match(/[\d,]+/);
        return m ? parseInt(m[0].replace(/,/g, ''), 10) : Infinity;
      };
      list.sort((a, b) => parsePrice(a.priceText) - parsePrice(b.priceText));
      break;
    }
    default:
      list.sort((a, b) => a.sortOrder - b.sortOrder || b.reviewCount - a.reviewCount);
      break;
  }

  const areaCounts = new Map<string, number>();
  partnerShopListMock.list.forEach((s) => {
    areaCounts.set(s.areaName, (areaCounts.get(s.areaName) || 0) + 1);
  });

  return {
    total: list.length,
    filters: {
      areas: Array.from(areaCounts.entries()).map(([areaName, count]) => ({ areaName, count })),
      quickFilters: partnerShopListMock.filters.quickFilters,
    },
    list,
  };
}

/* ─── Shared constants ─── */
const SORT_OPTIONS = [
  { key: 'recommended', label: '추천순' },
  { key: 'reviewCount', label: '후기 많은순' },
  { key: 'photoCount', label: '사진 많은순' },
  { key: 'directReviewCount', label: '직접 후기 많은순' },
  { key: 'priceAsc', label: '낮은 구성가순' },
] as const;

const PACKAGE_OPTIONS = [
  { key: 'all', label: '전체' },
  { key: 'custom', label: '맞춤 예복 중심' },
  { key: 'rental', label: '대여 가능' },
  { key: 'shooting', label: '촬영복 구성' },
  { key: 'parents', label: '혼주 예복 가능' },
  { key: 'premium', label: '프리미엄 맞춤' },
] as const;

const REVIEW_CRITERIA_OPTIONS = [
  { key: 'all', label: '전체' },
  { key: 'directReview', label: '직접 후기 많은 곳' },
  { key: 'photoReview', label: '사진 후기 많은 곳' },
  { key: 'consultSatisfaction', label: '상담 설명 만족 언급 많은 곳' },
  { key: 'shootingConfig', label: '촬영복 구성 언급 많은 곳' },
] as const;

const QUICK_CHIPS = [
  { key: 'all', label: '전체' },
  { key: 'cheongdam', label: '청담에서 상담받고 싶어요' },
  { key: 'shooting', label: '촬영복도 필요해요' },
  { key: 'premium', label: '프리미엄 맞춤 위주' },
  { key: 'rental', label: '대여 예복도 보고 싶어요' },
  { key: 'promotion', label: '계약 혜택 있는 곳' },
] as const;

/* ─── Components ─── */

function DesktopHeader() {
  return (
    <header className="hidden lg:flex items-center justify-between max-w-[1200px] mx-auto px-6 pt-6 pb-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-bold text-[#121B35]">예복 인증업체</h1>
        <span className="bg-[#121B35] text-white text-[11px] px-2.5 py-1 rounded-full font-medium">후기 기반 인증</span>
      </div>
    </header>
  );
}

function HeroSection() {
  const navigate = useNavigate();
  return (
    <div className="px-4 pt-3 pb-3 lg:max-w-[1200px] lg:mx-auto lg:px-6 lg:pt-2 lg:pb-6">
      <div className="lg:flex lg:items-start lg:justify-between lg:gap-8">
        <div className="lg:flex-1">
          <p className="hidden lg:block text-[13px] text-[#B9965B] font-medium tracking-wide uppercase">Certified Partner Shops</p>
          <h2 className="text-[18px] lg:text-[28px] font-bold text-[#121B35] mt-0 lg:mt-1 leading-tight">
            <span className="lg:hidden">후기와 사진으로 보는 예복 인증업체</span>
            <span className="hidden lg:inline">실제 후기와 사진으로 비교하는 예복 인증업체</span>
          </h2>
          <p className="text-[12px] lg:text-sm text-[#536079] mt-1.5 lg:mt-2 leading-relaxed max-w-[560px]">
            다이렉트 후기와 실제 사진을 바탕으로 상담 만족도, 촬영복 구성, 혜택까지 함께 확인해보세요.
          </p>
          <div className="flex items-center gap-2 lg:gap-3 mt-2.5 lg:mt-3 flex-wrap">
            <span className="text-[11px] lg:text-[13px] text-[#536079]">
              <span className="lg:hidden">아직 기준이 없다면</span>
              <span className="hidden lg:inline">어디서 해야 할지 모르겠다면, 먼저 내 예복 기준을 정리해보세요.</span>
            </span>
            <button
              onClick={() => navigate('/guide')}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 lg:px-3.5 lg:py-1.5 rounded-full text-[11px] lg:text-[13px] font-medium bg-[#121B35] text-white hover:bg-[#1A2342] transition-colors whitespace-nowrap"
            >
              <i className="ri-compass-3-line text-xs lg:text-sm" />
              내 예복 기준 찾기
            </button>
          </div>
          {/* Mobile stats — one-line chips */}
          <div className="flex flex-wrap gap-1.5 lg:gap-2 mt-3 lg:hidden">
            <span className="bg-white border border-[#E8E2DA] text-[#536079] text-[10px] px-2 py-0.5 rounded-full">40개 업체</span>
            <span className="bg-white border border-[#E8E2DA] text-[#536079] text-[10px] px-2 py-0.5 rounded-full">1,860+ 후기</span>
            <span className="bg-white border border-[#E8E2DA] text-[#536079] text-[10px] px-2 py-0.5 rounded-full">28곳 사진</span>
          </div>
        </div>
        <div className="hidden lg:flex lg:gap-3 lg:shrink-0">
          <div className="bg-white rounded-xl px-4 py-3 text-center min-w-[100px]">
            <p className="text-xl font-bold text-[#121B35]">40</p>
            <p className="text-[11px] text-[#536079] mt-0.5">인증업체</p>
          </div>
          <div className="bg-white rounded-xl px-4 py-3 text-center min-w-[100px]">
            <p className="text-xl font-bold text-[#121B35]">1,860+</p>
            <p className="text-[11px] text-[#536079] mt-0.5">분석 후기</p>
          </div>
          <div className="bg-white rounded-xl px-4 py-3 text-center min-w-[100px]">
            <p className="text-xl font-bold text-[#121B35]">28</p>
            <p className="text-[11px] text-[#536079] mt-0.5">곳 사진 보유</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickFilterChips({
  activeFilter,
  onSelect,
}: {
  activeFilter: string;
  onSelect: (key: string) => void;
}) {
  return (
    <div className="px-4 pb-3 lg:max-w-[1200px] lg:mx-auto lg:px-6 lg:pb-5">
      <div className="flex gap-2 overflow-x-auto no-scrollbar lg:flex-wrap">
        {QUICK_CHIPS.map((chip) => {
          const isActive = activeFilter === chip.key;
          return (
            <button
              key={chip.key}
              onClick={() => onSelect(chip.key)}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-[12px] lg:text-[13px] font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-[#121B35] text-white'
                  : 'bg-white border border-[#E8E2DA] text-[#536079] hover:border-[#B9965B] hover:text-[#121B35]'
              }`}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MobileFilterSheet({
  open,
  onClose,
  selectedArea,
  selectedPackageType,
  hasPromotionOnly,
  selectedReviewCriteria,
  onAreaChange,
  onPackageTypeChange,
  onPromotionToggle,
  onReviewCriteriaChange,
  onReset,
}: {
  open: boolean;
  onClose: () => void;
  selectedArea: string;
  selectedPackageType: string;
  hasPromotionOnly: boolean;
  selectedReviewCriteria: string;
  onAreaChange: (area: string) => void;
  onPackageTypeChange: (pkg: string) => void;
  onPromotionToggle: () => void;
  onReviewCriteriaChange: (criteria: string) => void;
  onReset: () => void;
}) {
  if (!open) return null;

  return (
    <div className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-8 max-h-[70dvh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[16px] font-semibold text-[#121B35]">필터</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F7F2EA]">
            <i className="ri-close-line text-[#536079] text-lg" />
          </button>
        </div>

        {/* 지역 */}
        <div className="mb-5">
          <h4 className="text-[12px] font-semibold text-[#121B35] mb-2.5">지역</h4>
          <div className="flex flex-wrap gap-2">
            {allAreas.map((area) => (
              <button
                key={area}
                onClick={() => onAreaChange(area)}
                className={`px-3.5 py-2 rounded-full text-[12px] font-medium transition-colors whitespace-nowrap ${
                  selectedArea === area ? 'bg-[#121B35] text-white' : 'bg-[#F7F2EA] text-[#536079] hover:bg-[#E8E2DA]'
                }`}
              >
                {area}
              </button>
            ))}
          </div>
        </div>

        {/* 원하는 구성 */}
        <div className="mb-5">
          <h4 className="text-[12px] font-semibold text-[#121B35] mb-2.5">원하는 구성</h4>
          <div className="flex flex-wrap gap-2">
            {PACKAGE_OPTIONS.map((p) => (
              <button
                key={p.key}
                onClick={() => onPackageTypeChange(p.key)}
                className={`px-3.5 py-2 rounded-full text-[12px] font-medium transition-colors whitespace-nowrap ${
                  selectedPackageType === p.key ? 'bg-[#121B35] text-white' : 'bg-[#F7F2EA] text-[#536079] hover:bg-[#E8E2DA]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* 후기 기준 */}
        <div className="mb-5">
          <h4 className="text-[12px] font-semibold text-[#121B35] mb-2.5">후기 기준</h4>
          <div className="flex flex-wrap gap-2">
            {REVIEW_CRITERIA_OPTIONS.map((rc) => (
              <button
                key={rc.key}
                onClick={() => onReviewCriteriaChange(rc.key)}
                className={`px-3.5 py-2 rounded-full text-[12px] font-medium transition-colors whitespace-nowrap ${
                  selectedReviewCriteria === rc.key ? 'bg-[#121B35] text-white' : 'bg-[#F7F2EA] text-[#536079] hover:bg-[#E8E2DA]'
                }`}
              >
                {rc.label}
              </button>
            ))}
          </div>
        </div>

        {/* 혜택 */}
        <div className="mb-6">
          <h4 className="text-[12px] font-semibold text-[#121B35] mb-2.5">혜택</h4>
          <button
            onClick={onPromotionToggle}
            className={`px-3.5 py-2 rounded-full text-[12px] font-medium transition-colors whitespace-nowrap ${
              hasPromotionOnly ? 'bg-[#121B35] text-white' : 'bg-[#F7F2EA] text-[#536079] hover:bg-[#E8E2DA]'
            }`}
          >
            계약 혜택 있는 업체
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2.5">
          <button
            onClick={() => { onReset(); onClose(); }}
            className="flex-1 py-3 rounded-xl text-[13px] font-medium text-[#536079] bg-[#F7F2EA] hover:bg-[#E8E2DA] transition-colors whitespace-nowrap"
          >
            초기화
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl text-[13px] font-medium text-white bg-[#121B35] hover:bg-[#1A2342] transition-colors whitespace-nowrap"
          >
            적용하기
          </button>
        </div>
      </div>
    </div>
  );
}

function DesktopFilterSidebar({
  selectedArea,
  selectedPackageType,
  hasPromotionOnly,
  selectedReviewCriteria,
  totalCount,
  onAreaChange,
  onPackageTypeChange,
  onPromotionToggle,
  onReviewCriteriaChange,
}: {
  selectedArea: string;
  selectedPackageType: string;
  hasPromotionOnly: boolean;
  selectedReviewCriteria: string;
  totalCount: number;
  onAreaChange: (area: string) => void;
  onPackageTypeChange: (pkg: string) => void;
  onPromotionToggle: () => void;
  onReviewCriteriaChange: (criteria: string) => void;
}) {
  const areaCountMap = useMemo(() => {
    const map = new Map<string, number>();
    partnerShopListMock.list.forEach((s) => map.set(s.areaName, (map.get(s.areaName) || 0) + 1));
    return map;
  }, []);

  return (
    <aside className="hidden lg:block w-[260px] shrink-0">
      <div className="sticky top-[88px] bg-white rounded-2xl p-5">
        <p className="text-[13px] text-[#536079] mb-4">
          전체 <span className="font-semibold text-[#121B35]">{totalCount}개</span> 업체
        </p>

        {/* 지역 */}
        <div className="mb-5">
          <h4 className="text-[11px] font-semibold text-[#121B35] uppercase tracking-wide mb-2.5">지역</h4>
          <div className="space-y-0.5">
            {allAreas.map((area) => {
              const count = area === '전체' ? partnerShopListMock.list.length : (areaCountMap.get(area) || 0);
              const isActive = selectedArea === area;
              return (
                <button
                  key={area}
                  onClick={() => onAreaChange(area)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-[13px] transition-colors ${
                    isActive ? 'bg-[#121B35] text-white font-medium' : 'text-[#536079] hover:bg-[#F7F2EA]'
                  }`}
                >
                  <span>{area}</span>
                  <span className={`text-[11px] ${isActive ? 'text-white/70' : 'text-[#B0B8C1]'}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 원하는 구성 */}
        <div className="mb-5">
          <h4 className="text-[11px] font-semibold text-[#121B35] uppercase tracking-wide mb-2.5">원하는 구성</h4>
          <div className="space-y-0.5">
            {PACKAGE_OPTIONS.map((p) => (
              <button
                key={p.key}
                onClick={() => onPackageTypeChange(p.key)}
                className={`w-full text-left px-3 py-2 rounded-lg text-[13px] transition-colors ${
                  selectedPackageType === p.key ? 'bg-[#121B35] text-white font-medium' : 'text-[#536079] hover:bg-[#F7F2EA]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* 후기 기준 */}
        <div className="mb-5">
          <h4 className="text-[11px] font-semibold text-[#121B35] uppercase tracking-wide mb-2.5">후기 기준</h4>
          <div className="space-y-0.5">
            {REVIEW_CRITERIA_OPTIONS.map((rc) => (
              <button
                key={rc.key}
                onClick={() => onReviewCriteriaChange(rc.key)}
                className={`w-full text-left px-3 py-2 rounded-lg text-[13px] transition-colors ${
                  selectedReviewCriteria === rc.key ? 'bg-[#121B35] text-white font-medium' : 'text-[#536079] hover:bg-[#F7F2EA]'
                }`}
              >
                {rc.label}
              </button>
            ))}
          </div>
        </div>

        {/* 혜택 */}
        <div>
          <h4 className="text-[11px] font-semibold text-[#121B35] uppercase tracking-wide mb-2.5">혜택</h4>
          <button
            onClick={onPromotionToggle}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-[13px] transition-colors ${
              hasPromotionOnly ? 'bg-[#121B35] text-white font-medium' : 'text-[#536079] hover:bg-[#F7F2EA]'
            }`}
          >
            <span>계약 혜택 있는 업체</span>
            <span className={`text-[11px] ${hasPromotionOnly ? 'text-white/70' : 'text-[#B0B8C1]'}`}>7</span>
          </button>
        </div>
      </div>
    </aside>
  );
}

function SortDropdown({
  selectedSort,
  onSortChange,
}: {
  selectedSort: string;
  onSortChange: (sort: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedLabel = SORT_OPTIONS.find((s) => s.key === selectedSort)?.label || '추천순';

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setShowTooltip(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative flex items-center gap-1.5">
      {selectedSort === 'recommended' && (
        <div className="relative">
          <button
            onClick={() => setShowTooltip(!showTooltip)}
            className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-[#F7F2EA] transition-colors"
            aria-label="추천순 설명"
          >
            <i className="ri-information-line text-[13px] text-[#B0B8C1]" />
          </button>
          {showTooltip && (
            <div className="absolute right-0 top-full mt-1.5 bg-[#121B35] text-white text-[11px] px-3 py-2 rounded-lg shadow-lg z-30 w-[200px] leading-relaxed">
              후기 수, 사진 수, 직접 후기 수를 종합해 정렬해요.
              <div className="absolute -top-1 right-2 w-2 h-2 rotate-45 bg-[#121B35]" />
            </div>
          )}
        </div>
      )}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 bg-white border border-[#E8E2DA] rounded-lg px-3 py-2 text-[13px] text-[#536079] hover:border-[#B9965B] transition-colors whitespace-nowrap"
      >
        <i className="ri-arrow-up-down-line text-xs" />
        {selectedLabel}
        <i className={`ri-arrow-down-s-line text-xs transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-[#E8E2DA] rounded-xl shadow-lg py-1 z-20 min-w-[150px]">
          {SORT_OPTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => { onSortChange(s.key); setOpen(false); }}
              className={`w-full text-left px-4 py-2.5 text-[13px] transition-colors whitespace-nowrap ${
                selectedSort === s.key ? 'bg-[#F7F2EA] text-[#121B35] font-medium' : 'text-[#536079] hover:bg-[#F7F2EA]'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl overflow-hidden animate-pulse">
      <div className="aspect-[16/10] bg-[#E8E2DA]" />
      <div className="p-3.5 lg:p-4 space-y-3">
        <div className="h-4 bg-[#E8E2DA] rounded w-3/4" />
        <div className="h-3 bg-[#E8E2DA] rounded w-1/2" />
        <div className="h-12 bg-[#E8E2DA] rounded-xl" />
        <div className="flex gap-3">
          <div className="h-3 bg-[#E8E2DA] rounded w-20" />
          <div className="h-3 bg-[#E8E2DA] rounded w-16" />
        </div>
        <div className="flex gap-2">
          <div className="h-5 bg-[#E8E2DA] rounded-full w-16" />
          <div className="h-5 bg-[#E8E2DA] rounded-full w-14" />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onReset }: { onReset: () => void }) {
  return (
    <div className="bg-white rounded-2xl p-8 text-center max-w-[400px] mx-auto mt-8">
      <div className="w-14 h-14 mx-auto bg-[#F7F2EA] rounded-full flex items-center justify-center mb-4">
        <i className="ri-search-line text-2xl text-[#B0B8C1]" />
      </div>
      <h3 className="text-[15px] font-semibold text-[#121B35]">조건에 맞는 인증업체가 없습니다.</h3>
      <p className="text-[12px] text-[#536079] mt-2 leading-relaxed">
        지역이나 구성 조건을 다시 선택해 주세요.
      </p>
      <button
        onClick={onReset}
        className="mt-4 px-5 py-2.5 bg-[#121B35] text-white text-[13px] font-medium rounded-lg hover:bg-[#1A2342] transition-colors whitespace-nowrap"
      >
        전체 업체 보기
      </button>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-white rounded-2xl p-8 text-center max-w-[400px] mx-auto mt-8">
      <div className="w-14 h-14 mx-auto bg-[#F7F2EA] rounded-full flex items-center justify-center mb-4">
        <i className="ri-error-warning-line text-2xl text-[#B9965B]" />
      </div>
      <h3 className="text-[15px] font-semibold text-[#121B35]">업체 목록을 불러오지 못했습니다.</h3>
      <p className="text-[12px] text-[#536079] mt-2 leading-relaxed">
        {message}
      </p>
      <button
        onClick={onRetry}
        className="mt-4 px-5 py-2.5 bg-[#121B35] text-white text-[13px] font-medium rounded-lg hover:bg-[#1A2342] transition-colors whitespace-nowrap"
      >
        다시 시도
      </button>
    </div>
  );
}

/* ─── Page ─── */

export default function PartnerShopListPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [shops, setShops] = useState<PartnerShopListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedArea, setSelectedArea] = useState('전체');
  const [selectedQuickFilter, setSelectedQuickFilter] = useState('all');
  const [selectedSort, setSelectedSort] = useState('recommended');
  const [selectedPackageType, setSelectedPackageType] = useState('all');
  const [hasPromotionOnly, setHasPromotionOnly] = useState(false);
  const [selectedReviewCriteria, setSelectedReviewCriteria] = useState('all');
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const fetchShops = async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const data = await getPartnerShopList({
        shopCategory: '예복',
        areaName: selectedArea,
        sort: selectedSort as 'recommended' | 'reviewCount' | 'directReviewCount' | 'photoCount' | 'priceAsc',
        hasPromotion: hasPromotionOnly,
        quickFilter: selectedQuickFilter !== 'all' ? selectedQuickFilter : undefined,
        packageType: selectedPackageType !== 'all' ? selectedPackageType : undefined,
        reviewCriteria: selectedReviewCriteria !== 'all' ? selectedReviewCriteria : undefined,
      });
      setShops(data.list);
      setTotalCount(data.total);
    } catch (err) {
      setErrorMessage('잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops();
  }, [selectedArea, selectedQuickFilter, selectedSort, selectedPackageType, hasPromotionOnly, selectedReviewCriteria]);

  const handleResetAll = () => {
    setSelectedArea('전체');
    setSelectedQuickFilter('all');
    setSelectedSort('recommended');
    setSelectedPackageType('all');
    setHasPromotionOnly(false);
    setSelectedReviewCriteria('all');
  };

  return (
    <div className="min-h-[100dvh] bg-[#F7F2EA]">
      <DesktopHeader />
      <HeroSection />
      <QuickFilterChips activeFilter={selectedQuickFilter} onSelect={setSelectedQuickFilter} />

      <div className="lg:max-w-[1200px] lg:mx-auto lg:px-6 lg:flex lg:gap-8 lg:items-start lg:pt-2">
        <DesktopFilterSidebar
          selectedArea={selectedArea}
          selectedPackageType={selectedPackageType}
          hasPromotionOnly={hasPromotionOnly}
          selectedReviewCriteria={selectedReviewCriteria}
          totalCount={totalCount}
          onAreaChange={setSelectedArea}
          onPackageTypeChange={setSelectedPackageType}
          onPromotionToggle={() => setHasPromotionOnly(!hasPromotionOnly)}
          onReviewCriteriaChange={setSelectedReviewCriteria}
        />

        <div className="flex-1 min-w-0">
          {/* Result header with filter & sort */}
          <div className="px-4 lg:px-0 pb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <p className="text-[12px] lg:text-sm text-[#536079]">
                <span className="font-semibold text-[#121B35]">{totalCount}개</span> 업체
                {selectedArea !== '전체' && <span className="text-[#B9965B]"> · {selectedArea}</span>}
              </p>
              <button
                onClick={() => setFilterSheetOpen(true)}
                className="lg:hidden flex items-center gap-1 bg-white border border-[#E8E2DA] rounded-full px-3 py-1.5 text-[11px] text-[#536079] hover:border-[#B9965B] transition-colors whitespace-nowrap"
              >
                <i className="ri-equalizer-line text-xs" />
                필터
              </button>
            </div>
            <p className="hidden lg:block text-[11px] text-[#B9965B]">후기와 사진을 바탕으로 정리했어요.</p>
            <SortDropdown selectedSort={selectedSort} onSortChange={setSelectedSort} />
          </div>

          {loading ? (
            <div className="px-4 lg:px-0 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 pb-16">
              {[1, 2, 3, 4, 5, 6].map((i) => <SkeletonCard key={i} />)}
            </div>
          ) : errorMessage ? (
            <ErrorState message={errorMessage} onRetry={fetchShops} />
          ) : shops.length === 0 ? (
            <EmptyState onReset={handleResetAll} />
          ) : (
            <div className="px-4 lg:px-0 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 pb-16">
              {shops.map((shop) => (
                <PartnerShopCard key={shop.id} shop={{...shop, shopSlug: 'hehehe'}} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="lg:hidden px-4 pb-6 text-center">
        <p className="text-[11px] text-[#B9965B]">후기와 사진을 바탕으로 정리된 인증업체</p>
      </div>

      <MobileFilterSheet
        open={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        selectedArea={selectedArea}
        selectedPackageType={selectedPackageType}
        hasPromotionOnly={hasPromotionOnly}
        selectedReviewCriteria={selectedReviewCriteria}
        onAreaChange={setSelectedArea}
        onPackageTypeChange={setSelectedPackageType}
        onPromotionToggle={() => setHasPromotionOnly(!hasPromotionOnly)}
        onReviewCriteriaChange={setSelectedReviewCriteria}
        onReset={handleResetAll}
      />
    </div>
  );
}